import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { scdMatchesFilter, scdSecondaryLabel, groupScds, catalogNameFromRxNorm, normalizeStrengthUnits } from './rxNormDisplay';

const API_BASE = `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/medication-catalog`;
const apiError = (error, fallback) => error.response?.data?.error || fallback;

const RxNormAddWizard = ({
  mode,
  medication,
  existingScdRxcuis = [],
  canOverride,
  headers,
  routes,
  dosageForms,
  onCancel,
  onSaved,
}) => {
  const existing = useMemo(
    () => new Set((existingScdRxcuis || []).map(String)),
    [existingScdRxcuis]
  );
  const linkedMedication = Boolean(medication?.rxcui);
  const startStep = mode === 'presentation' && linkedMedication ? 2 : 1;

  const [step, setStep] = useState(startStep);
  const [query, setQuery] = useState(medication?.name || '');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [ingredient, setIngredient] = useState(
    linkedMedication
      ? { rxcui: medication.rxcui, name: medication.rxnorm_name || medication.name, tty: medication.rxnorm_tty || 'IN' }
      : null
  );
  const [scds, setScds] = useState([]);
  const [scdFilter, setScdFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [parsed, setParsed] = useState([]);
  const [displayName, setDisplayName] = useState(medication?.name || '');
  const [notes, setNotes] = useState('');
  const [overrideMode, setOverrideMode] = useState(false);
  const [formOverride, setFormOverride] = useState(false);
  const [manual, setManual] = useState({
    name: medication?.name || '',
    strength: '',
    dosage_form: dosageForms[0] || 'Tablet',
    route: routes[0] || 'PO',
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const errorRef = useRef(null);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [error]);

  const loadScdsForIngredient = useCallback(async (rxcui) => {
    const response = await axios.get(`${API_BASE}/rxnorm/scds`, {
      headers,
      params: { rxcui },
    });
    setScds(response.data.scds || []);
    if (response.data.ingredient) {
      setIngredient(response.data.ingredient);
    }
  }, [headers]);

  useEffect(() => {
    if (mode === 'presentation' && linkedMedication) {
      loadScdsForIngredient(medication.rxcui).catch((err) => {
        setError(apiError(err, 'Could not load RxNorm presentations.'));
      });
    }
  }, [mode, linkedMedication, medication, loadScdsForIngredient]);

  const applySearchResult = useCallback(async (result, searchTerm, { autoAdvance = false } = {}) => {
    setSearchResult(result);
    const ingredients = result.ingredients || [];
    if (ingredients.length === 1 && autoAdvance) {
      const item = ingredients[0];
      setIngredient(item);
      setDisplayName((current) => current || catalogNameFromRxNorm(item.name));
      setBusy(true);
      try {
        if (result.scds?.length) {
          setScds(result.scds);
        } else {
          await loadScdsForIngredient(item.rxcui);
        }
        setStep(2);
      } catch (err) {
        setError(apiError(err, 'Could not load presentations for this drug.'));
      } finally {
        setBusy(false);
      }
      return;
    }
    if (ingredients.length !== 1) {
      setIngredient(null);
    } else {
      setIngredient(ingredients[0]);
    }
    setScds(result.scds || []);
  }, [loadScdsForIngredient]);

  const runSearch = useCallback(async (event, nextQuery = query, options = {}) => {
    event?.preventDefault();
    const searchTerm = String(nextQuery || '').trim();
    if (searchTerm.length < 3) return;
    setError('');
    setSearching(true);
    try {
      const response = await axios.get(`${API_BASE}/rxnorm/search`, {
        headers,
        params: { query: searchTerm },
      });
      await applySearchResult(response.data, searchTerm, options);
    } catch (err) {
      setSearchResult({ ingredients: [], scds: [] });
      setError(apiError(err, 'RxNorm search failed.'));
    } finally {
      setSearching(false);
    }
  }, [applySearchResult, headers, query]);

  useEffect(() => {
    if (step !== 1 || overrideMode) return;
    const searchTerm = query.trim();
    if (searchTerm.length < 3) return;
    const timer = setTimeout(() => {
      runSearch(null, searchTerm, { autoAdvance: false });
    }, 400);
    return () => clearTimeout(timer);
  }, [query, step, overrideMode, runSearch]);

  const pickIngredient = async (item) => {
    setIngredient(item);
    setDisplayName(catalogNameFromRxNorm(item.name));
    setError('');
    setBusy(true);
    try {
      if (!searchResult?.scds?.length || (searchResult.ingredients || []).length > 1) {
        await loadScdsForIngredient(item.rxcui);
      }
      setStep(2);
    } catch (err) {
      setError(apiError(err, 'Could not load presentations for this drug.'));
    } finally {
      setBusy(false);
    }
  };

  const toggleScd = (rxcui) => {
    const id = String(rxcui);
    if (existing.has(id)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const goReview = async () => {
    if (!selectedIds.size) {
      setError('Select the presentations this hospital stocks.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      const response = await axios.post(
        `${API_BASE}/rxnorm/presentations`,
        { scd_rxcuis: Array.from(selectedIds) },
        { headers }
      );
      setParsed((response.data || []).map((row) => ({
        ...row,
        strength: normalizeStrengthUnits(row.strength || ''),
        dosage_form: row.dosage_form || dosageForms[0] || 'Tablet',
        route: row.route || (row.dosage_form === 'Injection' ? 'IV' : 'PO'),
      })));
      setStep(3);
    } catch (err) {
      setError(apiError(err, 'Could not read presentation details from RxNorm.'));
    } finally {
      setBusy(false);
    }
  };

  const updateParsed = (index, field, value) => {
    setParsed((rows) => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const saveRxNorm = async () => {
    const incomplete = parsed.find((row) => !row.strength || !row.dosage_form || !row.route);
    if (incomplete) {
      setError('Fill strength, form, and route for every selected presentation.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (mode === 'medication') {
        const response = await axios.post(`${API_BASE}/medications`, {
          name: displayName || catalogNameFromRxNorm(ingredient.name),
          rxcui: ingredient.rxcui,
          notes: notes || null,
          presentations: parsed.map((row) => ({
            ...row,
            strength: normalizeStrengthUnits(row.strength),
          })),
        }, { headers });
        await onSaved(response.data);
      } else {
        const response = await axios.post(
          `${API_BASE}/medications/${medication.medication_id}/presentations`,
          {
            presentations: parsed.map((row) => ({
              ...row,
              strength: normalizeStrengthUnits(row.strength),
            })),
          },
          { headers }
        );
        await onSaved(response.data);
      }
    } catch (err) {
      setError(apiError(err, 'Could not save to the catalog.'));
    } finally {
      setBusy(false);
    }
  };

  const saveOverride = async (event) => {
    event.preventDefault();
    if (!canOverride) return;
    if (!notes.trim()) {
      setError(formOverride
        ? 'Add a short note explaining why this form is not in RxNorm.'
        : 'Add a short note explaining why this is not in RxNorm.');
      return;
    }
    if (formOverride && (!manual.strength || !manual.dosage_form || !manual.route)) {
      setError('Fill strength, form, and route.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (mode === 'medication') {
        const presentations = (formOverride || manual.strength)
          ? [{
              strength: normalizeStrengthUnits(manual.strength),
              dosage_form: manual.dosage_form,
              route: manual.route,
              rxnorm_override: true,
            }]
          : [];
        const payload = {
          name: displayName || catalogNameFromRxNorm(ingredient?.name) || manual.name || query,
          notes,
          presentations,
        };
        if (ingredient?.rxcui) {
          payload.rxcui = ingredient.rxcui;
        } else {
          payload.rxnorm_override = true;
        }
        const response = await axios.post(`${API_BASE}/medications`, payload, { headers });
        await onSaved(response.data);
      } else {
        const response = await axios.post(
          `${API_BASE}/medications/${medication.medication_id}/presentations`,
          {
            strength: normalizeStrengthUnits(manual.strength),
            dosage_form: manual.dosage_form,
            route: manual.route,
            notes,
            rxnorm_override: true,
          },
          { headers }
        );
        await onSaved(response.data);
      }
    } catch (err) {
      setError(apiError(err, 'Could not save the override.'));
    } finally {
      setBusy(false);
    }
  };

  const filteredScds = scds.filter((scd) => scdMatchesFilter(scd, scdFilter));
  const grouped = groupScds(filteredScds);
  const noMatch = searchResult && !searchResult.ingredients?.length && !searching;

  return (
    <div className="medication-catalog-modal-backdrop" onClick={onCancel}>
      <div
        className="medication-catalog-modal medication-catalog-form medication-catalog-wizard"
        onClick={(event) => event.stopPropagation()}
      >
        <h3>{mode === 'medication' ? 'Add medication from RxNorm' : 'Add presentation from RxNorm'}</h3>
        {error && (
          <div ref={errorRef} className="medication-catalog-status error medication-catalog-modal-error">
            {error}
          </div>
        )}

        {overrideMode ? (
          <form onSubmit={saveOverride}>
            <p className="medication-catalog-hint" style={{ padding: 0 }}>
              {formOverride
                ? 'The drug was found in RxNorm, but this strength and form will be saved without an RxNorm presentation code.'
                : 'This will be saved without an RxNorm code. Only use this when the drug is not in RxNorm.'}
            </p>
            {mode === 'medication' && !ingredient?.rxcui && (
              <label>
                Medication name
                <input
                  required
                  value={manual.name}
                  onChange={(event) => setManual((prev) => ({ ...prev, name: event.target.value }))}
                />
              </label>
            )}
            {mode === 'medication' && ingredient?.rxcui && (
              <label>
                Name to show in the catalog
                <input
                  required
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                />
              </label>
            )}
            {(mode === 'presentation' || formOverride || Boolean(manual.strength) || mode === 'medication') && (
              <>
                <label>
                  Strength {mode === 'medication' && !formOverride ? '(optional now)' : ''}
                  <input
                    required={mode === 'presentation' || formOverride}
                    placeholder="500 mg"
                    value={manual.strength}
                    onChange={(event) => setManual((prev) => ({ ...prev, strength: event.target.value }))}
                  />
                </label>
                <label>
                  Dosage form
                  <select
                    value={manual.dosage_form}
                    onChange={(event) => setManual((prev) => ({ ...prev, dosage_form: event.target.value }))}
                  >
                    {dosageForms.map((form) => <option key={form} value={form}>{form}</option>)}
                  </select>
                </label>
                <label>
                  Route
                  <select
                    value={manual.route}
                    onChange={(event) => setManual((prev) => ({ ...prev, route: event.target.value }))}
                  >
                    {routes.map((route) => <option key={route} value={route}>{route}</option>)}
                  </select>
                </label>
              </>
            )}
            <label>
              {formOverride ? 'Why is this form not in RxNorm?' : 'Why is this not in RxNorm?'}
              <textarea
                required
                rows={3}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </label>
            <div className="medication-catalog-modal-actions">
              <button
                type="button"
                className="medication-catalog-btn"
                onClick={() => {
                  setOverrideMode(false);
                  setFormOverride(false);
                }}
              >
                {formOverride ? 'Back' : 'Back to search'}
              </button>
              <button type="submit" className="medication-catalog-btn primary" disabled={busy}>{busy ? 'Saving...' : 'Save without RxNorm'}</button>
            </div>
          </form>
        ) : (
          <>
            {step === 1 && (
              <form onSubmit={(event) => runSearch(event, query, { autoAdvance: true })}>
                {mode === 'presentation' && !linkedMedication && (
                  <p className="medication-catalog-hint" style={{ padding: 0 }}>
                    This medication is not linked to RxNorm. Search for a match first.
                  </p>
                )}
                <label>
                  Search RxNorm
                  <input
                    type="search"
                    required
                    minLength={3}
                    placeholder="Paracetamol, ceftriaxone..."
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </label>
                <div className="medication-catalog-modal-actions">
                  <button type="button" className="medication-catalog-btn" onClick={onCancel}>Cancel</button>
                  <button type="submit" className="medication-catalog-btn primary" disabled={searching}>
                    {searching ? 'Searching...' : 'Search'}
                  </button>
                </div>
                {searchResult?.ingredients?.length > 0 && (
                  <div className="rxnorm-result-list">
                    <p className="medication-catalog-hint" style={{ padding: '12px 0 4px' }}>
                      {searchResult.approximated ? 'Closest RxNorm matches:' : 'Select the generic drug:'}
                    </p>
                    {searchResult.ingredients.map((item) => (
                      <button
                        key={item.rxcui}
                        type="button"
                        className="medication-catalog-row"
                        onClick={() => pickIngredient(item)}
                        disabled={busy}
                      >
                        <span className="medication-catalog-row-title">{item.name}</span>
                        <span className="medication-catalog-row-meta">RxCUI {item.rxcui} · {item.tty}{item.synonym ? ` · ${item.synonym}` : ''}</span>
                      </button>
                    ))}
                  </div>
                )}
                {(noMatch || (mode === 'presentation' && !linkedMedication && !searchResult?.ingredients?.length)) && (
                  <div className="medication-catalog-empty">
                    {noMatch ? 'Nothing in RxNorm matched this name.' : 'Not in RxNorm'}
                    {canOverride ? (
                      <div style={{ marginTop: 12 }}>
                        <button type="button" className="medication-catalog-btn" onClick={() => { setFormOverride(false); setManual((prev) => ({ ...prev, name: query || medication?.name || '' })); setOverrideMode(true); }}>
                          Not in RxNorm
                        </button>
                      </div>
                    ) : (
                      <p>Save is disabled. A supervisor with RxNorm override permission is needed to add it anyway.</p>
                    )}
                  </div>
                )}
              </form>
            )}

            {step === 2 && (
              <>
                <p className="medication-catalog-hint" style={{ padding: 0 }}>
                  {ingredient ? <><strong>{ingredient.name}</strong> (RxCUI {ingredient.rxcui}). </> : null}
                  Tick only the strengths this hospital stocks.
                </p>
                {mode === 'medication' && (
                  <label>
                    Name to show in the catalog
                    <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
                  </label>
                )}
                <label>
                  Filter presentations
                  <input
                    type="search"
                    placeholder="syrup, 100 mg/5 ml, tablet..."
                    value={scdFilter}
                    onChange={(event) => setScdFilter(event.target.value)}
                  />
                </label>
                <p className="medication-catalog-hint" style={{ padding: 0 }}>
                  All RxNorm presentations are listed. Liquids are often named Oral Suspension, not syrup.
                </p>
                <div className="rxnorm-scd-list">
                  {grouped.length === 0 && (
                    <div className="medication-catalog-empty">
                      No presentations match this filter. Try syrup, suspension, or 100 mg.
                    </div>
                  )}
                  {grouped.map(([doseForm, items]) => (
                    <div key={doseForm} className="rxnorm-scd-group">
                      <h4>{doseForm}</h4>
                      {items.map((scd) => {
                        const already = existing.has(String(scd.rxcui));
                        const secondary = scdSecondaryLabel(scd);
                        return (
                          <label key={scd.rxcui} className={`checkbox-row rxnorm-scd-item ${already ? 'inactive' : ''}`}>
                            <span className="rxnorm-scd-item-line">
                              <input
                                type="checkbox"
                                disabled={already}
                                checked={already || selectedIds.has(String(scd.rxcui))}
                                onChange={() => toggleScd(scd.rxcui)}
                              />
                              <span className="rxnorm-scd-item-name">
                                {scd.name}
                                {already ? ' (already in catalog)' : ''}
                              </span>
                            </span>
                            {secondary ? (
                              <span className="medication-catalog-row-meta rxnorm-scd-item-meta">{secondary}</span>
                            ) : null}
                          </label>
                        );
                      })}
                    </div>
                  ))}
                </div>
                {canOverride ? (
                  <button
                    type="button"
                    className="medication-catalog-choice"
                    onClick={() => {
                      setFormOverride(true);
                      setManual((prev) => ({
                        ...prev,
                        name: displayName || ingredient?.name || medication?.name || query,
                        strength: '',
                        dosage_form: dosageForms[0] || 'Tablet',
                        route: routes[0] || 'PO',
                      }));
                      setOverrideMode(true);
                    }}
                  >
                    <span className="medication-catalog-choice-title">This form is not listed</span>
                    <span className="medication-catalog-choice-meta">
                      The drug is in RxNorm, but this strength or form is not. Add it by hand.
                    </span>
                  </button>
                ) : (
                  <p className="medication-catalog-hint" style={{ padding: '8px 0 0' }}>
                    If the form you need is not listed, a supervisor with RxNorm override permission can add it by hand.
                  </p>
                )}
                <div className="medication-catalog-modal-actions">
                  {!(mode === 'presentation' && linkedMedication) && (
                    <button type="button" className="medication-catalog-btn" onClick={() => setStep(1)}>Back</button>
                  )}
                  {(mode === 'presentation' && linkedMedication) && (
                    <button type="button" className="medication-catalog-btn" onClick={onCancel}>Cancel</button>
                  )}
                  <button type="button" className="medication-catalog-btn primary" onClick={goReview} disabled={busy || selectedIds.size === 0}>
                    {busy ? 'Loading...' : `Review ${selectedIds.size || ''}`}
                  </button>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <p className="medication-catalog-hint" style={{ padding: 0 }}>
                  Check strength, form, and route. Confirm injection routes (IV vs IM).
                </p>
                {parsed.map((row, index) => (
                  <div key={row.rxcui} className="rxnorm-review-card">
                    <div className="medication-catalog-row-meta">{row.rxnorm_name} · RxCUI {row.rxcui}</div>
                    {row.needs_review && <span className="medication-catalog-badge warn">Check form/route</span>}
                    {/suspension|solution/i.test(row.dose_form || '') && (
                      <div className="medication-catalog-row-meta">Hospital bottles are often labeled syrup. Change the form if needed.</div>
                    )}
                    <label>
                      Strength
                      <input
                        required
                        value={row.strength}
                        onChange={(event) => updateParsed(index, 'strength', event.target.value)}
                      />
                    </label>
                    <label>
                      Dosage form
                      <select
                        value={row.dosage_form}
                        onChange={(event) => updateParsed(index, 'dosage_form', event.target.value)}
                      >
                        {dosageForms.map((form) => <option key={form} value={form}>{form}</option>)}
                      </select>
                    </label>
                    <label>
                      Route
                      <select
                        value={row.route}
                        onChange={(event) => updateParsed(index, 'route', event.target.value)}
                      >
                        {routes.map((route) => <option key={route} value={route}>{route}</option>)}
                      </select>
                    </label>
                  </div>
                ))}
                <div className="medication-catalog-modal-actions">
                  <button type="button" className="medication-catalog-btn" onClick={() => setStep(2)}>Back</button>
                  <button type="button" className="medication-catalog-btn primary" onClick={saveRxNorm} disabled={busy}>
                    {busy ? 'Saving...' : 'Save to catalog'}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RxNormAddWizard;
